import { useCallback } from 'react';
import { ConfirmModal, SimLocationModal } from '../../components';
import { useSimActions } from '../../hooks';
import { PracticeProspect } from '../../types';

export enum SimActionType {
  ARCHIVE = 'ARCHIVE',
  DELETE = 'DELETE',
  MOVE = 'MOVE',
}

interface SimActionModalsProps {
  simModal: { type: SimActionType; prospect: PracticeProspect };
  closeModal: () => void;
  onDeleteSuccess?: () => void;
  onArchiveSuccess?: () => void;
}

const SimActionModals = ({ simModal, closeModal, onDeleteSuccess, onArchiveSuccess }: SimActionModalsProps) => {
  const {
    type,
    prospect: { personaId, firstName, lastName, folderId, isDraft },
  } = simModal;

  const fullName = `${firstName} ${lastName}`;

  const {
    runArchive,
    runDelete,
    runEdit,
    isArchiving: isArchivingSimulation,
    isDeleting: isDeletingSimulation,
    isEditing: isEditingSimulation,
  } = useSimActions();

  const onConfirmArchive = useCallback(() => {
    runArchive({
      id: personaId,
      onSuccess: () => {
        onArchiveSuccess?.();
        closeModal();
      },
    });
  }, [closeModal, onArchiveSuccess, personaId, runArchive]);

  const onConfirmDelete = useCallback(() => {
    runDelete({
      id: personaId,
      onSuccess: () => {
        onDeleteSuccess?.();
        closeModal();
      },
    });
  }, [closeModal, onDeleteSuccess, personaId, runDelete]);

  const onMove = useCallback(
    (newFolderId: string) => {
      runEdit({
        payload: { folderId: newFolderId, id: personaId },
        onSuccess: closeModal,
      });
    },
    [closeModal, personaId, runEdit]
  );

  const deleteProps = {
    buttonText: 'Delete',
    title: 'Delete simulation',
    confirmText: (
      <>
        Are you sure you want to delete the simulation <span className="font-medium">{fullName}</span>? This action
        cannot be undone.
      </>
    ),
  };

  const archiveProps = {
    buttonText: 'Archive',
    title: 'Archive simulation',
    confirmText: (
      <>
        Are you sure you want to archive the simulation <span className="font-medium">{fullName}</span>? You will have
        the option to unarchive them later.
      </>
    ),
  };

  return (
    <>
      <ConfirmModal
        destructive
        isOpen={type === SimActionType.ARCHIVE}
        setIsOpen={closeModal}
        onConfirm={onConfirmArchive}
        isLoading={isArchivingSimulation}
        // If the simulation is a draft, we show the delete modal text and button
        // Because for users it's a delete action, but it's actually an archive action if prospect is used
        {...(isDraft ? deleteProps : archiveProps)}
      />
      <ConfirmModal
        destructive
        isOpen={type === SimActionType.DELETE}
        setIsOpen={closeModal}
        onConfirm={onConfirmDelete}
        isLoading={isDeletingSimulation}
        {...deleteProps}
      />
      {/* We are conditionally rendering the modal so we do not fetch the folder path when the user is not moving the simulation. */}
      {type === SimActionType.MOVE && (
        <SimLocationModal
          isOpen
          closeModal={closeModal}
          onSubmit={onMove}
          isSubmitting={isEditingSimulation}
          currFolderId={folderId}
          title="Move simulation"
          submitText="Move"
        />
      )}
    </>
  );
};

export default SimActionModals;
